import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9n4rom8mbnljin59tp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请检查网络连接后重试');
      setFormStatus('error');
    }
  };
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '97影院',
    url: 'https://www.97cinema.com',
    description: '97影院是领先的免费高清影视在线观看平台，提供海量高清电影、热门电视剧全集免费在线观看服务。',
    foundingDate: '2019',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9797',
      contactType: 'customer service',
      email: 'contact@97cinema.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '朝阳区影视产业园A座',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '97影院是否需要注册才能观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '完全不需要！97影院所有影片均可免费在线观看，无需注册、无需登录，打开即看。我们致力于为用户提供最便捷的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '97影院的画质如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '97影院提供超清蓝光画质，最高支持4K分辨率。我们采用先进的视频编码技术和CDN加速节点，确保画面清晰细腻，色彩还原真实。',
        },
      },
      {
        '@type': 'Question',
        name: '播放会缓冲卡顿吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们部署了全球CDN加速网络，智能选择最优节点为您服务。在正常网络环境下，影片加载迅速、播放流畅不卡顿。建议使用5M以上宽带获得最佳体验。',
        },
      },
      {
        '@type': 'Question',
        name: '97影院的影片更新速度如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的内容团队7×24小时不间断更新，院线新片下线后第一时间上线，热门电视剧同步卫视更新。确保您不错过任何精彩内容。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '97影院全面支持PC、平板、手机等各类设备。响应式设计自动适配不同屏幕尺寸，无论您使用什么设备，都能获得优质的观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: '关于我们' },
                  ]}
                />
                <div className="pb-6">
                  <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">关于我们</h1>
                </div>
              </div>
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-500 mb-4">
                  <i className="ri-movie-2-fill text-background-50 text-2xl"></i>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">品牌介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                  97影院成立于2019年，是国内领先的免费高清影视在线观看平台。
                  我们始终秉持"让每个人都享受高清影视"的理念，为广大用户提供海量优质的影视内容。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
                <div className="p-6 rounded-lg bg-background-50 border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className="ri-film-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">海量内容</h3>
                  <p className="text-xs text-foreground-500">收录超过10万部高清影片，涵盖电影、电视剧、综艺、动漫、纪录片等全品类内容。</p>
                </div>
                <div className="p-6 rounded-lg bg-background-50 border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className="ri-4k-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">超清画质</h3>
                  <p className="text-xs text-foreground-500">支持蓝光超清画质播放，先进的H.265编码技术，让每一帧都清晰动人。</p>
                </div>
                <div className="p-6 rounded-lg bg-background-50 border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100">
                    <i className="ri-shield-check-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">流畅体验</h3>
                  <p className="text-xs text-foreground-500">全球CDN加速，智能码率适配，确保不同网络环境下都能流畅播放不缓冲。</p>
                </div>
              </div>

              <div className="mb-12 p-8 rounded-xl bg-background-100">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 text-center">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed text-center max-w-2xl mx-auto">
                  在信息爆炸的时代，97影院致力于成为连接优质影视内容与观众的桥梁。
                  我们相信，每个人都应当享有免费欣赏优秀影视作品的权利。
                  通过技术创新和持续的内容建设，我们让高清影视触手可及，让每一个故事都能被更多人看见。
                </p>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-8 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '影视爱好者小张',
                    role: '资深影迷',
                    content: '97影院真的是我见过最好用的免费影视站！画质超清，更新速度也快，新上映的大片基本上很快就能看到。已经推荐给身边所有朋友了。',
                    rating: 5,
                  },
                  {
                    name: '追剧达人Linda',
                    role: '电视剧迷',
                    content: '追剧神器！热门电视剧同步更新，而且全集免费看，画质蓝光级别。最棒的是播放超级流畅，从来不会卡顿，体验感满分。',
                    rating: 5,
                  },
                  {
                    name: '技术宅阿强',
                    role: 'IT从业者',
                    content: '从技术角度来看，97影院的CDN加速和视频编码做得相当专业。在移动网络下也能流畅播放高清视频，技术实力确实过硬。',
                    rating: 5,
                  },
                ].map((review, idx) => (
                  <div key={idx} className="p-6 rounded-lg bg-background-50 border border-background-200">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.content}</p>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">邮箱</h4>
                        <p className="text-sm text-foreground-500">contact@97cinema.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                        <i className="ri-phone-line text-accent-600"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">客服热线</h4>
                        <p className="text-sm text-foreground-500">400-888-9797</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                        <i className="ri-map-pin-line text-secondary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">公司地址</h4>
                        <p className="text-sm text-foreground-500">北京市朝阳区影视产业园A座</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">工作时间</h4>
                        <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <form className="p-6 rounded-lg bg-background-100 border border-background-200" data-readdy-form onSubmit={handleSubmit}>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">发送消息</h3>
                    <div className="space-y-3">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          required
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言内容..."
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          required
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                      </div>
                      <div className="anti-spam-field">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {formStatus === 'submitting' ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i>发送中...
                          </span>
                        ) : (
                          <>
                            <i className="ri-send-plane-line mr-2"></i>发送消息
                          </>
                        )}
                      </button>
                      {formStatus === 'success' && (
                        <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 text-center">
                          <i className="ri-checkbox-circle-line mr-1"></i>消息已发送成功，我们会尽快回复您！
                        </div>
                      )}
                      {formStatus === 'error' && formError && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 text-center">
                          <i className="ri-error-warning-line mr-1"></i>{formError}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-8 text-center">常见问题</h2>
              <div className="space-y-3">
                {[
                  { q: '97影院是否需要注册才能观看？', a: '完全不需要！97影院所有影片均可免费在线观看，无需注册、无需登录，打开即看。我们致力于为用户提供最便捷的观影体验。' },
                  { q: '97影院的画质如何？', a: '97影院提供超清蓝光画质，最高支持4K分辨率。我们采用先进的视频编码技术和CDN加速节点，确保画面清晰细腻，色彩还原真实。' },
                  { q: '播放会缓冲卡顿吗？', a: '我们部署了全球CDN加速网络，智能选择最优节点为您服务。在正常网络环境下，影片加载迅速、播放流畅不卡顿。建议使用5M以上宽带获得最佳体验。' },
                  { q: '97影院的影片更新速度如何？', a: '我们的内容团队7×24小时不间断更新，院线新片下线后第一时间上线，热门电视剧同步卫视更新。确保您不错过任何精彩内容。' },
                  { q: '支持哪些设备观看？', a: '97影院全面支持PC、平板、手机等各类设备。响应式设计自动适配不同屏幕尺寸，无论您使用什么设备，都能获得优质的观影体验。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                      <span className="font-heading text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}